import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined
} from "@ant-design/icons"
import { Skeleton, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import type { InvoicesSummary } from "../types/invoices.types.ts"

interface InvoicesKpiRowProps {
  summary: InvoicesSummary
  loading?: boolean
  failed?: boolean
}

const sum = (...values: Array<string | number | null | undefined>): number =>
  values.reduce<number>((total, value) => total + (Number(value) || 0), 0)

/**
 * Four compact horizontal tiles, not dashboard-sized `KpiCard`s — this list
 * page needs a glanceable money summary, not a drillable widget.
 *
 * The four figures are not four slices of one pie, and the layout says so:
 * Total and its two receivable buckets sit together, Paid is divided off. See
 * `docs/kb/decisions/2026-08-13-invoice-snapshot-paid-is-a-separate-measure.md`.
 * Overdue is a subset of Outstanding, not a peer of it, so it carries a hint
 * rather than reading as a fifth independent number.
 */
export function InvoicesKpiRow({
  summary,
  loading,
  failed
}: InvoicesKpiRowProps) {
  const { t } = useTranslation()

  const tiles = [
    {
      key: "total",
      label: t("invoices.kpi.totalInvoiced"),
      value: summary.total,
      icon: <FileTextOutlined />,
      tone: "text-primary bg-primary-bg"
    },
    {
      key: "outstanding",
      label: t("invoices.kpi.outstanding"),
      hint: t("invoices.kpi.outstandingHint"),
      value: sum(summary.pending, summary.overdue),
      icon: <ClockCircleOutlined />,
      tone: "text-warning bg-warning-bg"
    },
    {
      key: "overdue",
      label: t("invoices.kpi.overdue"),
      hint: t("invoices.kpi.overdueHint"),
      value: summary.overdue,
      icon: <ExclamationCircleOutlined />,
      tone: "text-error bg-error-bg"
    },
    {
      key: "paid",
      label: t("invoices.kpi.paid"),
      hint: t("invoices.kpi.paidHint"),
      value: summary.paid,
      icon: <CheckCircleOutlined />,
      tone: "text-success bg-success-bg",
      apart: true
    }
  ]

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={`flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3.5 py-3 ${
            tile.apart ? "md:ml-3 md:border-l-4 md:border-l-gray-200" : ""
          }`}
        >
          <span
            className={`flex size-8 shrink-0 items-center justify-center rounded-full text-sm ${tile.tone}`}
          >
            {tile.icon}
          </span>
          <div className="min-w-0">
            <Tooltip title={tile.hint}>
              <div className="truncate text-xs font-medium text-gray-500">
                {tile.label}
              </div>
            </Tooltip>
            {loading ? (
              <Skeleton.Input active size="small" style={{ width: 70 }} />
            ) : (
              <div className="text-lg font-semibold leading-tight text-gray-900">
                {failed ? t("invoices.kpi.unavailable") : budgetFormat(tile.value)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
